import { FilterAlt, MoreVert } from "@mui/icons-material";
// import { Button, Chip, FormControlLabel, Grid, IconButton, Menu, MenuItem, Paper, Radio, RadioGroup, Slider, TextField, Typography, useTheme } from "@mui/material";
import { Autocomplete, Button, Chip, CircularProgress, FormControlLabel, Grid, IconButton, Menu, MenuItem, Paper, Radio, RadioGroup, Slider, TextField, Typography, useTheme, Tooltip, Popover, Divider } from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { intervalLabel } from "../../common";
import { openDialog } from "../../features/dialog";
import { enqueueSnack } from "../../features/snackbar";
import postfilters from "../../config/postfilters";
import FilterBySelect from "../common/FilterBySelect";
import LanguageSelect from "../common/LanguageSelect";
import PrioritySelect from "../common/PrioritySelect";
import SentimentSelect from "../common/SentimentSelect";
import PlatformSelect from "../common/PlatformSelect";
import TagFilterOperatorSelect from "../common/TagFilterOperatorSelect";
import TagFilterSelect from "../common/TagFilterSelect";
import JobFilterSelect from "../common/JobFilterSelect";
import JobFilterOperatorSelect from "../common/JobFilterOperatorSelect";
import { useDispatch } from "react-redux";

const GEO_LIMITS = { places: 10, countries: 10, total: 20, radiusMin: 1, radiusMax: 1000, radiusDefault: 25 };
const clampRadius = (val) => {
    const num = Number(val);
    if (Number.isNaN(num)) return GEO_LIMITS.radiusDefault;
    return Math.min(GEO_LIMITS.radiusMax, Math.max(GEO_LIMITS.radiusMin, num));
};

const geoOptionKey = (o, idx = "") => {
    if (!o) return `geo-null-${idx}`;
    const label = o.label ?? o.value ?? "NULL";
    const type = o.type ?? "unknown";
    const latPart = o.lat != null ? o.lat : "x";
    const lonPart = o.lon != null ? o.lon : "x";
    return `${type}:${label}:${latPart}:${lonPart}`;
};

const dedupeGeoOptions = (opts = []) => {
    const seen = new Map();
    opts.forEach((o, idx) => {
        if (!o) return;
        const key = geoOptionKey(o, idx);
        if (!seen.has(key)) {
            seen.set(key, o);
        }
    });
    return Array.from(seen.values());
};

const normalizeGeoValue = (value = {}) => {
    const mode = value.mode === "distance" ? "distance" : "name";
    const nameNode = value.name || {};
    const places = Array.isArray(nameNode.places) ? nameNode.places.slice(0, GEO_LIMITS.places) : [];
    const countries = Array.isArray(nameNode.countries) ? nameNode.countries.slice(0, GEO_LIMITS.countries) : [];
    const op = (nameNode.op || "OR").toUpperCase() === "AND" ? "AND" : "OR";

    const distanceNode = value.distance || {};
    const centerRaw = distanceNode.center;
    const hasCenter = centerRaw && centerRaw.lat !== "" && centerRaw.lat != null && centerRaw.lon !== "" && centerRaw.lon != null;
    const center = hasCenter ? {
        label: centerRaw.label,
        lat: Number(centerRaw.lat),
        lon: Number(centerRaw.lon)
    } : null;
    const radiusKm = clampRadius(distanceNode.radiusKm ?? distanceNode.radius ?? GEO_LIMITS.radiusDefault);

    return {
        mode,
        name: {
            op,
            places,
            countries
        },
        distance: {
            center,
            radiusKm
        }
    };
};

function PostFilterInlinePanel({
    data, 
    tab, 
    onChange, 
    project, 
    tagShortenThreshold = 3,
    textMaxChars = 40,
    debugFilterTypes = false
}){ 

    const [filters, _setFilters] = useState({});

    const [addForm, setAddForm] = useState({});

    const [geoState, setGeoState] = useState(normalizeGeoValue());
    const [geoOptions, setGeoOptions] = useState([]);
    const [geoLoading, setGeoLoading] = useState(false);
    const [geoQuery, setGeoQuery] = useState("");

    const [newFilterOpen, setNewFilterOpen] = useState(false);

    const [preview, setPreview] = useState({
      open: false,
      anchorEl: null,
      title: "",
      content: null,
      onEdit: null
    });

    // const theme = useTheme();

    const theme = useTheme();
    const dispatch = useDispatch();
    const truncate = (s = "", max = 30) =>
      (typeof s === "string" && s.length > max) ? (s.slice(0, max) + "…") : s;

    const geoIsEmpty = (geo) => {
        if (!geo) return true;
        if (geo.mode === "distance") {
            return !(geo.distance && geo.distance.center && geo.distance.center.lat != null && geo.distance.center.lon != null);
        }
        const nameNode = geo.name || {};
        return !(Array.isArray(nameNode.places) && nameNode.places.length) && !(Array.isArray(nameNode.countries) && nameNode.countries.length);
    };

    const buildGeoSelectedOptions = (geo) => {
        const nameNode = geo && geo.name ? geo.name : {};
        const places = Array.isArray(nameNode.places) ? nameNode.places : [];
        const countries = Array.isArray(nameNode.countries) ? nameNode.countries : [];
        return [
            ...places.map(v => ({ type: "place", label: v, value: v })),
            ...countries.map(v => ({ type: "country", label: v, value: v }))
        ];
    };

    const openPreview = (title, content, anchorEl, onEdit) =>
      setPreview({ open: true, anchorEl, title, content, onEdit });
    
    const closePreview = () =>
      setPreview({ open: false, anchorEl: null, title: "", content: null, onEdit: null });

    // Build id->label lookup from project.tags
    const tagById = useMemo(() => {
    const map = new Map();
    (project?.tags || []).forEach(t => {
        if (t && (t.id != null)) map.set(String(t.id), t.label || String(t.id));
    });
    return map;
    }, [project?.tags]);

    // Resolve filter.value (IDs or objects) into display names
    const resolveTagNames = (vals) => {
    const arr = Array.isArray(vals) ? vals : [];
    return arr
        .map(v => {
        if (v == null) return "";
        if (typeof v === "string" || typeof v === "number") {
            const key = String(v);
            return tagById.get(key) ?? key; // fallback to id string if label missing
        }
        // object forms: {id,label} or {value,label} etc.
        const id = v.id ?? v.value ?? v.tagId;
        const label = v.label ?? v.name ?? v.title ?? (id != null ? tagById.get(String(id)) : "");
        return label || (id != null ? String(id) : "");
        })
        .filter(Boolean);
    };

    const normalizeOperator = (raw) => {
        // Map UI / backend variants to canonical ops
        const op = (raw || "OR").toString().toUpperCase();
        if (op === "DEFAULT" || op === "ANY" || op === "AT_LEAST_ONE") return "OR";
        if (op === "ALL") return "AND";
        if (op === "NONE") return "NOT";            // if you have such a value
        if (op === "NOTSTRICT") return "NOTSTRICT"; // keep as is
        if (op === "NOT") return "NOT";
        if (op === "AND" || op === "OR") return op;
        return "OR"; // safe fallback
        };

        const joinerAndPrefix = (canonicalOp) => {
        // How to render between tags (joiner) and before the list (prefix)
        switch (canonicalOp) {
            case "AND":       return { joiner: "AND", prefix: "" };
            case "OR":        return { joiner: "OR",  prefix: "" };
            case "NOT":       return { joiner: "OR",  prefix: "NOT " };        // NOT (A OR B)
            case "NOTSTRICT": return { joiner: "AND", prefix: "NOT " };        // NOT (A AND B)
            default:          return { joiner: "OR",  prefix: "" };
        }
        };

    const warnGeoLimit = (message) => {
        dispatch(enqueueSnack({
            message,
            options: { variant: 'warning' }
        }));
    };

    const handleGeoNameChange = (options) => {
        const nextPlaces = options.filter(o => o.type === "place").map(o => o.value || o.label).filter(Boolean);
        const nextCountries = options.filter(o => o.type === "country").map(o => o.value || o.label).filter(Boolean);

        if (nextPlaces.length > GEO_LIMITS.places) {
            warnGeoLimit(`You can select up to ${GEO_LIMITS.places} places`);
            return;
        }
        if (nextCountries.length > GEO_LIMITS.countries) {
            warnGeoLimit(`You can select up to ${GEO_LIMITS.countries} countries`);
            return;
        }
        if ((nextPlaces.length + nextCountries.length) > GEO_LIMITS.total) {
            warnGeoLimit(`You can select up to ${GEO_LIMITS.total} geo items in total`);
            return;
        }

        setGeoState(prev => normalizeGeoValue({
            ...prev,
            mode: "name",
            name: {
                ...prev.name,
                places: Array.from(new Set(nextPlaces)),
                countries: Array.from(new Set(nextCountries))
            }
        }));
    };

    const handleGeoCenterSelect = (option) => {
        if (!option || option.lat == null || option.lon == null) {
            setGeoState(prev => ({
                ...prev,
                mode: "distance",
                distance: { ...prev.distance, center: null }
            }));
            return;
        }

        setGeoState(prev => ({
            ...prev,
            mode: "distance",
            distance: {
                ...prev.distance,
                center: {
                    label: option.label || option.value,
                    lat: option.lat,
                    lon: option.lon
                }
            }
        }));
    };

    const handleGeoManualCenter = (key, value) => {
        setGeoState(prev => {
            const nextCenter = { ...(prev.distance.center || {}) };
            nextCenter[key] = value === "" ? "" : Number(value);
            return {
                ...prev,
                mode: "distance",
                distance: {
                    ...prev.distance,
                    center: nextCenter
                }
            };
        });
    };

    const handleGeoRadiusChange = (value) => {
        setGeoState(prev => ({
            ...prev,
            mode: "distance",
            distance: {
                ...prev.distance,
                radiusKm: clampRadius(value)
            }
        }));
    };

    const handleGeoModeChange = (mode) => {
        if (mode === "distance") {
            setGeoState(prev => normalizeGeoValue({
                ...prev,
                mode: "distance"
            }));
        } else {
            setGeoState(prev => normalizeGeoValue({
                ...prev,
                mode: "name"
            }));
        }
    };

    const handleClearGeo = () => {
        const reset = normalizeGeoValue({ mode: "name", name: { op: "OR", places: [], countries: [] } });
        setGeoState(reset);
        if (filters.geo) {
            const newFilters = { ...filters };
            delete newFilters.geo;
            setFilters(newFilters);
        }
    };

    
    const setFilters = filters => {
        _setFilters(filters);
        onChange && onChange(filters);
    }

    useEffect(() => {
        _setFilters(data);
    },[data]);

    useEffect(() => {
        if(addForm.field === "geo"){
            setGeoState(normalizeGeoValue(addForm.value));
        }
    }, [addForm.field, addForm.value]);

    const loadGeoSuggestions = useCallback(async (query) => {
        if(!project || !project.id){
            return;
        }
        setGeoLoading(true);
        try{
            const params = {
                project_id: project.id
            };
            if(query){
                params.q = query;
            }
            const startParam = filters.starttimestamp || (filters.date && filters.date.start);
            const endParam = filters.endtimestamp || (filters.date && filters.date.end);
            if(startParam){
                params.starttimestamp = startParam;
            }
            if(endParam){
                params.endtimestamp = endParam;
            }
            const resp = await axios.get('/geo/suggest', { params });
            const suggestions = resp?.data?.suggestions || [];
            setGeoOptions(dedupeGeoOptions(suggestions));
        }catch(e){
            dispatch(enqueueSnack({
                message: e.message || 'Unable to load geo suggestions',
                options: { variant: 'error' }
            }));
        }finally{
            setGeoLoading(false);
        }
    }, [project, filters, dispatch]);

    useEffect(() => {
        if(addForm.field !== "geo") return;
        const timer = setTimeout(() => {
            loadGeoSuggestions(geoQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [geoQuery, addForm.field, loadGeoSuggestions]);

    useEffect(() => {
        if(addForm.field === "geo"){
            loadGeoSuggestions(geoQuery);
        }
    }, [addForm.field, loadGeoSuggestions, geoQuery]);

    const handleDeleteFilter = type => {
        const newFilters = {...filters}
        delete newFilters[type]
        setFilters(newFilters);
    }

    const selectedGeoCenterOption = geoState.distance && geoState.distance.center ? {
        type: "place",
        label: geoState.distance.center.label || `${geoState.distance.center.lat}, ${geoState.distance.center.lon}`,
        value: geoState.distance.center.label || `${geoState.distance.center.lat}, ${geoState.distance.center.lon}`,
        lat: geoState.distance.center.lat,
        lon: geoState.distance.center.lon
    } : null;

    const renderFilter = (filterType, filter, index) => {
        const postFilter = postfilters[filterType];
        if(!postFilter){
            // console.error("Non trovo " + filter.type);
            return null;
        }
        const ChipIcon = postFilter.icon;
        const uniqueKey = `${filterType}-${index}`;

        if (debugFilterTypes) {
            console.log("Filter chip:", {
                type: filterType,
                filter,
                labelFromConfig: postfilters[filterType]?.label?.(filter, project)
            });
        }

        // Defaults from config
        let chipLabel = postFilter.label(filter, project) || "Any";
        let tooltipText = undefined;
        let onContextMenu = undefined;

        // Special handling: TEXT search (ellipsis + tooltip + optional popover)
        if (filterType === "text_search") {
          const fullText = typeof filter === "string" ? filter : (filter?.text ?? String(filter ?? ""));
          const isLong = fullText.length > textMaxChars;
          chipLabel = isLong ? truncate(fullText, textMaxChars) : fullText || "Any";
          if (fullText) tooltipText = fullText;
          // Right-click (or long-press on mobile) to preview full text without opening the editor
          onContextMenu = (e) => {
            e.preventDefault();
            if (!fullText) return;
            openPreview(
              "Text filter",
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{fullText}</Typography>,
              e.currentTarget,
              () => handleOpenFilterSelector(filterType, filter)
            );
          };
        }

        // Special handling: TAGS (shorten to "FirstTag OPERATOR (Y more)" when count > N)

        if (filterType === "tag") {
            const type = filter?.type || "default";             // "default" | "reverse"
            const canonicalOp = normalizeOperator(filter?.operator);
            const { joiner, prefix } = joinerAndPrefix(canonicalOp);
            const values = filter?.value ?? [];
            const tagNames = resolveTagNames(values);

            if (type === "reverse") {
                chipLabel = "Without tags";
                tooltipText = "Without tags";
                onContextMenu = undefined;
            } else {
                if (tagNames.length === 0) {
                chipLabel = "Any";
                tooltipText = undefined;
                onContextMenu = undefined;
                } else if (tagNames.length > tagShortenThreshold) {
                const first = truncate(tagNames[0], textMaxChars);
                const restCount = tagNames.length - 1;
                // Prefix (NOT ) should appear BEFORE the list
                chipLabel = `${prefix}${first} ${joiner} (${restCount} more)`;
                tooltipText = `${prefix}(${tagNames.join(` ${joiner} `)})`;

                onContextMenu = (e) => {
                    e.preventDefault();
                    openPreview(
                    prefix ? `Exclude tags (${joiner})` : `Tags (${joiner})`,
                    <Box sx={{ maxWidth: 480, maxHeight: 260, overflow: "auto", p: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {tagNames.map((name, i) => (
                        <Chip key={`${name}-${i}`} size="small" label={name} sx={{ m: 0.25 }} />
                        ))}
                    </Box>,
                    e.currentTarget,
                    () => handleOpenFilterSelector(filterType, filter)
                    );
                };
                } else {
                // <= N tags: show entire expression, with prefix BEFORE the list
                const joined = tagNames.join(` ${joiner} `);
                chipLabel = prefix ? `${prefix}${joined}` : joined;
                tooltipText = prefix ? `${prefix}(${joined})` : joined;

                // Optional peek on right-click
                onContextMenu = (e) => {
                    e.preventDefault();
                    openPreview(
                    prefix ? `Exclude tags (${joiner})` : `Tags (${joiner})`,
                    <Box sx={{ maxWidth: 480, maxHeight: 260, overflow: "auto", p: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {tagNames.map((name, i) => (
                        <Chip key={`${name}-${i}`} size="small" label={name} sx={{ m: 0.25 }} />
                        ))}
                    </Box>,
                    e.currentTarget,
                    () => handleOpenFilterSelector(filterType, filter)
                    );
                };
                }
            }
        } // end if special case tags

        // Special handling: JOB (shorten to "FirstJob OR (Y more)" when count > N)
        if (filterType === "job") {
            // Jobs use simplified operators - only OR and NOT make sense since posts have single job
            const jobOp = filter?.operator || "default";
            const isNot = jobOp === "not";
            const joiner = "OR"; // Always OR for jobs since posts can match any of the selected jobs
            const prefix = isNot ? "NOT " : "";
            
            const values = filter?.value ?? [];
            const jobNames = values
                .map(id => {
                    const job = project?.jobs?.find(j => j.id === id);
                    return job ? job.label : `Job ${id}`;
                })
                .filter(Boolean);

            if (jobNames.length === 0) {
                chipLabel = "Any";
                tooltipText = undefined;
                onContextMenu = undefined;
            } else if (jobNames.length === 1) {
                // Single job - no need for operators
                chipLabel = `${prefix}${jobNames[0]}`;
                tooltipText = `${prefix}${jobNames[0]}`;
                onContextMenu = undefined;
            } else if (jobNames.length > tagShortenThreshold) {
                const first = truncate(jobNames[0], textMaxChars);
                const restCount = jobNames.length - 1;
                chipLabel = `${prefix}${first} ${joiner} (${restCount} more)`;
                tooltipText = `${prefix}(${jobNames.join(` ${joiner} `)})`;

                onContextMenu = (e) => {
                    e.preventDefault();
                    openPreview(
                        isNot ? `Exclude jobs` : `Jobs`,
                        <Box sx={{ maxWidth: 480, maxHeight: 260, overflow: "auto", p: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {jobNames.map((name, i) => (
                                <Chip key={`${name}-${i}`} size="small" label={name} sx={{ m: 0.25 }} />
                            ))}
                        </Box>,
                        e.currentTarget,
                        () => handleOpenFilterSelector(filterType, filter)
                    );
                };
            } else {
                // <= N jobs: show entire expression
                const joined = jobNames.join(` ${joiner} `);
                chipLabel = `${prefix}${joined}`;
                tooltipText = `${prefix}(${joined})`;

                // Optional peek on right-click
                onContextMenu = (e) => {
                    e.preventDefault();
                    openPreview(
                        isNot ? `Exclude jobs` : `Jobs`,
                        <Box sx={{ maxWidth: 480, maxHeight: 260, overflow: "auto", p: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {jobNames.map((name, i) => (
                                <Chip key={`${name}-${i}`} size="small" label={name} sx={{ m: 0.25 }} />
                            ))}
                        </Box>,
                        e.currentTarget,
                        () => handleOpenFilterSelector(filterType, filter)
                    );
                };
            }
        } // end if special case jobs

        //handle date range case 

        const isDate = filterType === "date";

        const chipSx = isDate
        ? { m: 0.5 } // no maxWidth, no ellipsis for dates
        : {
            m: 0.5,
            maxWidth: 250,
            "& .MuiChip-label": {
                maxWidth: 230,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            };

        const chipEl = (
        <Chip
            onClick={() => handleOpenFilterSelector(filterType, filter)}
            onContextMenu={onContextMenu}
            sx={chipSx}
            icon={<ChipIcon />}
            label={chipLabel}
            onDelete={() => handleDeleteFilter(filterType)}
        />
        );


        return tooltipText ? <Tooltip key={uniqueKey} title={tooltipText}>{chipEl}</Tooltip> : <span key={uniqueKey}>{chipEl}</span>;
    }

    const handleOpenFilterSelector = (field, value) => {
        setNewFilterOpen(true);
        if(!field || typeof field !== "string"){
            field = "text_search"
        }
        if(field === "geo"){
            setGeoState(normalizeGeoValue(value));
            setGeoQuery("");
        }
        setAddForm({
            field,
            value
        });
    }
    const handleApplyNewFilter = () => {
        setNewFilterOpen(false);
        if(addForm.field === "geo"){
            const normalized = normalizeGeoValue(geoState);
            if(geoIsEmpty(normalized)){
                handleDeleteFilter("geo");
                return;
            }
            setFilters({
                ...filters,
                geo: normalized
            });
            return;
        }

        if(addForm.value){
            setFilters({
                ...filters,
                [addForm.field]: addForm.value
            })
        }else{
            handleDeleteFilter(addForm.field);
        }
        
    }
    const handleCancelNewFilter = () => {
        setNewFilterOpen(false);
    }
    
    return <Box 
        position="relative"
        display="flex"
        
        alignItems="center"
        alignContent={"center"}
        justifyContent="space-between"

        sx={{
            height: 88,
            borderBottom: 1,
            borderBottomColor: theme.palette.primary.main
        }}
    >
        {/* left side: label + chips */}

        <Box display="flex" alignItems="center">
            <Typography variant="subtitle2" mr={1} sx={{width: 120}}>
                {Object.keys(filters).length > 0 ? "Active filters:" : "No filter set."}
            </Typography>
            <Box display="flex" alignItems="center" flexWrap={"wrap"} flexDirection="rows">            
                {Object.keys(filters).map((filterType, index) => renderFilter(filterType, filters[filterType], index))}
            </Box>
        </Box>

        {/* add filter paper */}
        <Paper 
            elevation={3}
            sx={{
                display: newFilterOpen ? 'flex' : "none",
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'absolute',
                zIndex: 1060,
                bgcolor: 'white',
                right: -1,
                top: 86,
                width: 800,
                height: 360,
                maxHeight: '70vh',
                p: 2,
                overflowY: 'auto'
            }}
        >
            <Grid container spacing={2}>
                <Grid item sm={4}>
                    <FilterBySelect
                        value={addForm.field || ""}
                        onChange={e => setAddForm({...addForm, field: e, value: filters[e]})}
                    />
                </Grid>
                <Grid item sm={0.5}></Grid>
                <Grid item sm={7}>
                    {addForm.field === "text_search" && <TextField
                        fullWidth
                        size="small"
                        label="Text"
                        value={addForm.value || ""}
                        onChange={e => setAddForm({...addForm, value: e.target.value})}
                    />}

                    {addForm.field === "sentiment" && <SentimentSelect
                        fullWidth
                        showDontChange={false}
                        showAny={true}
                        size="small"
                        value={addForm.value || ""}
                        onChange={e => setAddForm({...addForm, value: e})}
                    />}

                    {addForm.field === "platform" && <PlatformSelect
                        fullWidth
                        size="small"
                        value={addForm.value || ""}
                        onChange={e => setAddForm({...addForm, value: e})}
                    />}

                    {addForm.field === "priority" && <PrioritySelect
                        fullWidth
                        size="small"
                        value={addForm.value || ""}
                        onChange={e => setAddForm({...addForm, value: e})}
                    />}

                    {addForm.field === "language" && <LanguageSelect
                        fullWidth
                        size="small"
                        value={addForm.value || ""}
                        onChange={e => setAddForm({...addForm, value: e})}
                    />}

                    {addForm.field === "author" &&  <TextField
                        fullWidth
                        size="small"
                        label="Author"
                        value={addForm.value || ""}
                        onChange={e => setAddForm({...addForm, value: e.target.value})}
                    />}

                    {addForm.field === "date" && <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <TextField
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                type="date"
                                label="From"
                                value={addForm.value ? addForm.value.start || "" : ""}
                                onChange={e => setAddForm({
                                    ...addForm, 
                                    value: {...addForm.value || {}, start: e.target.value}
                                })}
                            />

                        </Grid>
                        <Grid item sm={12}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                label="To"
                                value={addForm.value ? addForm.value.end || "" : ""}
                                onChange={e => setAddForm({
                                    ...addForm, 
                                    value: {...addForm.value || {}, end: e.target.value}
                                })}
                            />
                        </Grid>
                    </Grid>}

                    {addForm.field === "interval" && <Grid container spacing={2} mt={0.1}>
                        <Grid item sm={12} >
                            <Slider
                                valueLabelDisplay="on"
                                marks={[{
                                    value: 1,
                                    label: 'a day'
                                },{
                                    value: 360,
                                    label: 'a year'
                                }]}
                                value={addForm.value || 1}
                                min={1}
                                step={1}
                                max={360}
                                getAriaValueText={value => intervalLabel(value)}
                                valueLabelFormat={value => intervalLabel(value)}
                                onChange={e => setAddForm({...addForm, value: e.target.value})}
                                
                            />

                        </Grid>
                    </Grid>}


                    {addForm.field === "tag" && <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <RadioGroup 
                                row
                                value={(addForm.value && addForm.value.type) || "default"}
                                onChange={e => setAddForm({
                                    ...addForm,
                                    value: {
                                        ...addForm.value || {},
                                        value: e.target.value === "reverse" ? ["9999"] : [],
                                        type: e.target.value
                                    }
                                })}
                            >
                                <FormControlLabel value="default" control={<Radio size="small" />} label="Filter posts with tags" />
                                <FormControlLabel value="reverse" control={<Radio size="small" />} label="Filter posts without tags" />
                            </RadioGroup>
                        </Grid>
                        {(!addForm.value || addForm.value.type!=="reverse") && [
                            <Grid item sm={12} key={1}>
                                <TagFilterSelect
                                    fullWidth
                                    value={addForm.value ? addForm.value.value || [] : []}
                                    onChange={e => setAddForm({
                                        ...addForm, 
                                        value: {...addForm.value || {}, value: e}
                                    })}
                                />

                            </Grid>,
                            <Grid item sm={12} key={2}>
                                <TagFilterOperatorSelect
                                    fullWidth
                                    value={addForm.value ? addForm.value.operator || "" : ""}
                                    onChange={e => setAddForm({
                                        ...addForm, 
                                        value: {...addForm.value || {}, operator: e}
                                    })}
                                />
                            </Grid>
                        ]}
                    </Grid>}

                    {addForm.field === "job" && <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <JobFilterSelect
                                fullWidth
                                value={addForm.value ? addForm.value.value || [] : []}
                                onChange={e => setAddForm({
                                    ...addForm, 
                                    value: {...addForm.value || {}, value: e}
                                })}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <JobFilterOperatorSelect
                                fullWidth
                                value={addForm.value ? addForm.value.operator || "" : ""}
                                onChange={e => setAddForm({
                                    ...addForm, 
                                    value: {...addForm.value || {}, operator: e}
                                })}
                            />
                        </Grid>
                    </Grid>}

                    {addForm.field === "geo" && <Grid container spacing={2}>
                        <Grid item sm={12}>
                            <RadioGroup 
                                row
                                value={geoState.mode}
                                onChange={e => handleGeoModeChange(e.target.value)}
                            >
                                <FormControlLabel value="name" control={<Radio size="small" />} label="By name" />
                                <FormControlLabel value="distance" control={<Radio size="small" />} label="By distance" />
                            </RadioGroup>
                        </Grid>

                        {geoState.mode === "name" && <>
                            <Grid item sm={12}>
                                <Autocomplete
                                    multiple
                                    loading={geoLoading}
                                    options={dedupeGeoOptions([...geoOptions, ...buildGeoSelectedOptions(geoState)])}
                                    filterSelectedOptions
                                    value={buildGeoSelectedOptions(geoState)}
                                    onChange={(e, v) => handleGeoNameChange(v)}
                                    onInputChange={(e, v) => setGeoQuery(v)}
                                    getOptionLabel={(o) => o.label || o.value || ""}
                                    isOptionEqualToValue={(o, v) => o.type === v.type && (o.value || o.label) === (v.value || v.label)}
                                    renderOption={(props, option) => (
                                        <li {...props} key={geoOptionKey(option)}>
                                            <Box display="flex" justifyContent="space-between" width="100%">
                                                <span>{option.label || option.value}</span>
                                                <Typography variant="caption" color="text.secondary">{option.type}</Typography>
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            label="Place or country"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {geoLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item sm={12}>
                                <RadioGroup 
                                    row
                                    value={geoState.name.op}
                                    onChange={e => setGeoState(prev => normalizeGeoValue({
                                        ...prev,
                                        mode: "name",
                                        name: { ...prev.name, op: e.target.value === "AND" ? "AND" : "OR" }
                                    }))}
                                >
                                </RadioGroup>
                            </Grid>
                        </>}

                        {geoState.mode === "distance" && <>
                            <Grid item sm={12}>
                                <Autocomplete
                                    loading={geoLoading}
                                    options={dedupeGeoOptions([...(geoOptions || []).filter(o => o.lat != null && o.lon != null), ...(selectedGeoCenterOption ? [selectedGeoCenterOption] : [])])}
                                    value={selectedGeoCenterOption}
                                    onChange={(e, v) => handleGeoCenterSelect(v)}
                                    onInputChange={(e, v) => setGeoQuery(v)}
                                    getOptionLabel={(o) => o.label || o.value || ""}
                                    isOptionEqualToValue={(o, v) => (o.lat === v.lat && o.lon === v.lon)}
                                    renderOption={(props, option) => (
                                        <li {...props} key={geoOptionKey(option)}>
                                            <Box display="flex" justifyContent="space-between" width="100%">
                                                <span>{option.label || option.value}</span>
                                                <Typography variant="caption" color="text.secondary">{option.type}</Typography>
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            label="Center (autocomplete)"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {geoLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Latitude"
                                    inputProps={{ min: -90, max: 90, step: 0.0001 }}
                                    value={(geoState.distance && geoState.distance.center && geoState.distance.center.lat !== undefined) ? geoState.distance.center.lat : ""}
                                    onChange={e => handleGeoManualCenter('lat', e.target.value)}
                                />
                            </Grid>
                            <Grid item sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Longitude"
                                    inputProps={{ min: -180, max: 180, step: 0.0001 }}
                                    value={(geoState.distance && geoState.distance.center && geoState.distance.center.lon !== undefined) ? geoState.distance.center.lon : ""}
                                    onChange={e => handleGeoManualCenter('lon', e.target.value)}
                                />
                            </Grid>
                            <Grid item sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Radius (km)"
                                    inputProps={{ min: GEO_LIMITS.radiusMin, max: GEO_LIMITS.radiusMax }}
                                    value={geoState.distance ? geoState.distance.radiusKm : GEO_LIMITS.radiusDefault}
                                    onChange={e => handleGeoRadiusChange(e.target.value)}
                                    helperText={`Between ${GEO_LIMITS.radiusMin}-${GEO_LIMITS.radiusMax} km (default ${GEO_LIMITS.radiusDefault} km)`}
                                />
                            </Grid>
                        </>}

                    </Grid>}
                </Grid>
            </Grid>
            <Box display="flex" justifyContent={"flex-end"} gap={2}>
                {addForm.field === "geo" && <Button size="small" variant="outlined" onClick={handleClearGeo}>Clear Geo</Button>}
                <Button onClick={handleCancelNewFilter} variant="contained" color="primary">Cancel</Button>
                <Button onClick={handleApplyNewFilter} variant="contained" color="secondary">Apply filter</Button>
            </Box>
        </Paper>

        {/* right side: Add filter + Tools */}

        <Box display="flex" alignItems="center" justifyContent="center">
            <Button 
                sx={{
                    borderRadius: 4,
                    width: 160

                }} 
                onClick={handleOpenFilterSelector} 
                size="small" 
                variant="contained" 
                color="secondary"
            >
                <FilterAlt />
                Add filter
            </Button>
            <ToolsMenu project={project} tab={tab} />
        </Box>

{/* Preview popover (opened via right-click on a chip) */}
    <Popover
      open={preview.open}
      anchorEl={preview.anchorEl}
      onClose={closePreview}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { p: 1.5, maxWidth: 520 } }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {preview.title}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      <Box>{preview.content}</Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, gap: 1 }}>
        {preview.onEdit && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              preview.onEdit();
              closePreview();
            }}
          >
            Edit filter
          </Button>
        )}
        <Button size="small" onClick={closePreview}>Close</Button>
      </Box>
    </Popover>

    </Box>;
}


function ToolsMenu({project, tab}){
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleMenuClick = (dialog) => {
        handleClose();
        dispatch(openDialog(dialog));
    }
    return <>
        <IconButton
            aria-controls={open ? 'add-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
        >
            <MoreVert color="primary" fontSize={"medium"} />
        </IconButton>
        <Menu
            id="add-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
            'aria-labelledby': 'add-button',
            }}
        >
            <MenuItem onClick={() => handleMenuClick({id: "editDashboard", props: tab.id})}>Edit Dashboard</MenuItem>
            <MenuItem onClick={() => handleMenuClick("dataset")}>View dataset</MenuItem>
            <MenuItem onClick={() => handleMenuClick("downloadCsv")}>Download CSV</MenuItem>
        </Menu>

    </>
    
}

export default PostFilterInlinePanel;